import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ClickAwayListener, InputBase, Popper, Typography } from '@mui/material';
import { Close, KeyboardArrowDown } from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

export type DocBasis = 'physical' | 'potential';

export interface DocRangeValue {
    min: string;
    max: string;
    basis: DocBasis;
    none: boolean;
}

export const EMPTY_DOC_RANGE: DocRangeValue = { min: '', max: '', basis: 'physical', none: false };

export const isDocRangeActive = (v: DocRangeValue) => v.none || v.min !== '' || v.max !== '';

// Query params for the API. A single number typed in both boxes ("30" to "30") means that whole
// day (30.00–30.99); a lone Min means "at least", a lone Max means "at most".
export const docRangeParams = (v: DocRangeValue, withBasis: boolean): Record<string, string> => {
    if (v.none) return { docNone: 'true', ...(withBasis ? { docBasis: v.basis } : {}) };
    if (!isDocRangeActive(v)) return {};
    const out: Record<string, string> = {};
    if (v.min !== '') out.docMin = v.min;
    if (v.max !== '') out.docMax = v.min !== '' && v.min === v.max ? String(Number(v.max) + 0.99) : v.max;
    if (withBasis) out.docBasis = v.basis;
    return out;
};

const PRESETS: { label: string; hint: string; min: string; max: string }[] = [
    { label: 'Critical', hint: '≤ 15', min: '', max: '15' },
    { label: 'Below target', hint: '15–60', min: '15', max: '60' },
    { label: 'Healthy', hint: '60+', min: '60', max: '' },
];

const describe = (v: DocRangeValue): string => {
    if (v.none) return 'No data';
    if (v.min !== '' && v.max !== '') return v.min === v.max ? `${v.min} d` : `${v.min} – ${v.max} d`;
    if (v.min !== '') return `≥ ${v.min} d`;
    if (v.max !== '') return `≤ ${v.max} d`;
    return 'All';
};

interface DocRangeFilterProps {
    value: DocRangeValue;
    onChange: (next: DocRangeValue) => void;
    /** Show the Physical / Potential switch (Simple/Bundle view has both). */
    showBasis?: boolean;
    minWidth?: number;
}

const DocRangeFilter: React.FC<DocRangeFilterProps> = ({ value, onChange, showBasis = false, minWidth = 170 }) => {
    const reduceMotion = useReducedMotion();
    const anchorRef = useRef<HTMLDivElement>(null);
    const minRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<DocRangeValue>(value);
    const active = isDocRangeActive(value);

    useEffect(() => {
        if (open) {
            setDraft(value);
            const t = setTimeout(() => minRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const invalid = !draft.none && draft.min !== '' && draft.max !== '' && Number(draft.min) > Number(draft.max);
    const summary = useMemo(() => {
        if (invalid) return 'Min can’t be higher than Max';
        if (draft.none) return 'Only rows with no DOC data';
        if (draft.min !== '' && draft.max !== '') return draft.min === draft.max ? `Exactly day ${draft.min} (${draft.min}.0 – ${draft.min}.99)` : `DOC from ${draft.min} to ${draft.max} days`;
        if (draft.min !== '') return `DOC of ${draft.min} days or more`;
        if (draft.max !== '') return `DOC of ${draft.max} days or less`;
        return 'Showing every DOC — set a Min and/or Max';
    }, [draft, invalid]);

    const apply = (next: DocRangeValue) => {
        onChange(next);
        setOpen(false);
    };
    const setNum = (key: 'min' | 'max', raw: string) => {
        const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        setDraft(d => ({ ...d, [key]: cleaned, none: false }));
    };
    const presetActive = (p: typeof PRESETS[number]) => !draft.none && draft.min === p.min && draft.max === p.max;

    const field = (key: 'min' | 'max', label: string) => (
        <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8A8078', mb: 0.4 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, height: 38, borderRadius: '10px', border: '1px solid', borderColor: invalid ? '#dc2626' : '#ECECE4', bgcolor: '#fff', transition: 'border-color 150ms, box-shadow 150ms', '&:focus-within': { borderColor: invalid ? '#dc2626' : '#DC5829', boxShadow: `0 0 0 3px ${invalid ? 'rgba(220,38,38,0.12)' : 'rgba(220,88,41,0.12)'}` } }}>
                <InputBase
                    inputRef={key === 'min' ? minRef : undefined}
                    value={draft[key]}
                    onChange={(e) => setNum(key, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !invalid) apply(draft); }}
                    placeholder={key === 'min' ? 'e.g. 30' : 'e.g. 40'}
                    inputProps={{ inputMode: 'decimal', 'aria-label': `${label} DOC in days` }}
                    sx={{ flex: 1, fontSize: '0.9rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: '#8A8078', fontWeight: 600 }}>days</Typography>
            </Box>
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', minWidth }}>
                <Box
                    ref={anchorRef}
                    onClick={() => setOpen(o => !o)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } else if (e.key === 'Escape') setOpen(false); }}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, height: 36, px: 1.25,
                        borderRadius: '10px', bgcolor: '#fff', border: '1px solid',
                        borderColor: open || active ? 'primary.main' : '#ECECE4',
                        boxShadow: open ? '0 0 0 3px rgba(220, 88, 41, 0.12)' : 'none',
                        cursor: 'pointer', outline: 'none', transition: 'border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover': { borderColor: '#DC5829' },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                        <Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#8A8078', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                            DOC{showBasis && active ? ` · ${value.basis === 'potential' ? 'Pot' : 'Phys'}` : ''}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: active ? 700 : 500, color: active ? '#1A1410' : 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {describe(value)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        {active && (
                            <Box
                                component="span"
                                role="button"
                                aria-label="Clear DOC filter"
                                onClick={(e) => { e.stopPropagation(); onChange({ ...EMPTY_DOC_RANGE, basis: value.basis }); }}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', color: 'text.secondary', '&:hover': { bgcolor: '#F3F2EC', color: '#DC5829' } }}
                            >
                                <Close sx={{ fontSize: 14 }} />
                            </Box>
                        )}
                        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18, ease: 'easeOut' }} style={{ display: 'flex' }}>
                            <KeyboardArrowDown sx={{ fontSize: 20, color: 'text.secondary' }} />
                        </motion.div>
                    </Box>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                >
                    <>
                        <AnimatePresence>
                            {open && (
                                <motion.div
                                    role="dialog"
                                    aria-label="Filter by DOC range"
                                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.97 }}
                                    animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.98 }}
                                    transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                                    style={{ transformOrigin: 'top left' }}
                                >
                                    <Box sx={{ width: 320, p: 1.75, borderRadius: '14px', bgcolor: '#fff', border: '1px solid #ECECE4', boxShadow: '0 18px 44px rgba(26, 20, 15, 0.18)' }}>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, mb: 1.25 }}>Days of cover (DOC)</Typography>

                                        {showBasis && (
                                            <Box role="radiogroup" aria-label="DOC type" sx={{ display: 'flex', p: '3px', mb: 1.5, borderRadius: '999px', bgcolor: 'rgba(26, 20, 15, 0.06)' }}>
                                                {(['physical', 'potential'] as DocBasis[]).map(b => {
                                                    const sel = draft.basis === b;
                                                    return (
                                                        <Box
                                                            key={b}
                                                            component="button"
                                                            type="button"
                                                            role="radio"
                                                            aria-checked={sel}
                                                            onClick={() => setDraft(d => ({ ...d, basis: b }))}
                                                            sx={{ position: 'relative', flex: 1, height: 28, border: 0, bgcolor: 'transparent', borderRadius: '999px', font: 'inherit', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', color: sel ? '#1A140F' : 'rgba(26,20,15,0.55)', '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                                        >
                                                            {sel && (
                                                                <motion.span
                                                                    layoutId="doc-basis-pill"
                                                                    transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 38 }}
                                                                    style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#fff', boxShadow: '0 1px 3px rgba(26,20,15,0.18)', zIndex: 0 }}
                                                                />
                                                            )}
                                                            <span style={{ position: 'relative', zIndex: 1 }}>{b === 'physical' ? 'Physical' : 'Potential'}</span>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                            {field('min', 'Min')}
                                            <Typography sx={{ pb: 1, color: '#8A8078', fontWeight: 700 }}>–</Typography>
                                            {field('max', 'Max')}
                                        </Box>

                                        <Box sx={{ minHeight: 20, mt: 0.9 }} aria-live="polite">
                                            <AnimatePresence mode="wait" initial={false}>
                                                <motion.div
                                                    key={summary}
                                                    initial={reduceMotion ? false : { opacity: 0, y: 3 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.12 }}
                                                >
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: invalid ? '#dc2626' : 'text.secondary' }}>{summary}</Typography>
                                                </motion.div>
                                            </AnimatePresence>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                                            {PRESETS.map(p => {
                                                const sel = presetActive(p);
                                                return (
                                                    <Box
                                                        key={p.label}
                                                        component="button"
                                                        type="button"
                                                        onClick={() => setDraft(d => ({ ...d, min: p.min, max: p.max, none: false }))}
                                                        sx={{ px: 1.1, height: 26, borderRadius: '999px', border: '1px solid', borderColor: sel ? '#DC5829' : '#DAD6CC', bgcolor: sel ? 'rgba(220, 88, 41, 0.1)' : '#fff', color: sel ? '#B8451E' : 'text.secondary', font: 'inherit', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', transition: 'all 140ms', '&:hover': { borderColor: '#DC5829' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                                    >
                                                        {p.label} <Box component="span" sx={{ opacity: 0.65, fontWeight: 600 }}>{p.hint}</Box>
                                                    </Box>
                                                );
                                            })}
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => setDraft(d => ({ ...d, min: '', max: '', none: !d.none }))}
                                                sx={{ px: 1.1, height: 26, borderRadius: '999px', border: '1px solid', borderColor: draft.none ? '#DC5829' : '#DAD6CC', bgcolor: draft.none ? 'rgba(220, 88, 41, 0.1)' : '#fff', color: draft.none ? '#B8451E' : 'text.secondary', font: 'inherit', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', '&:hover': { borderColor: '#DC5829' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 } }}
                                            >
                                                No data
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75 }}>
                                            <Button size="small" onClick={() => apply({ ...EMPTY_DOC_RANGE, basis: draft.basis })} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary', '&:hover': { color: '#DC5829' } }}>
                                                Clear
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                disabled={invalid}
                                                onClick={() => apply(draft)}
                                                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.25, bgcolor: '#DC5829', '&:hover': { bgcolor: '#B8451E' } }}
                                            >
                                                Apply
                                            </Button>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default DocRangeFilter;
